import { setAccessTokenProvider } from '../api/client';
import { decodeAccessToken, expiresAt } from './jwt';
import type { PolicySubject, Role } from './policy';

/**
 * Where the session lives (docs/07-implementation-roadmap.md F1.1, F1.2).
 *
 * **The access token is held in a module variable and nowhere else** — not
 * `localStorage`, not `sessionStorage`, not a cookie this code can read
 * (docs/04 §1.1, docs/90 §3). It is lost on reload, which is correct: the
 * HttpOnly refresh cookie is what survives, and `restore()` trades it for a new
 * access token at boot. ESLint bans the storage APIs outright so this cannot
 * regress by convenience (F-DoD-6).
 *
 * The store is deliberately **outside React**. The single-flight refresh in
 * `src/api/refresh.ts` runs from a fetch middleware — no component, no hook, no
 * render — and it has to be able to install a new token and to clear the session
 * when the refresh fails. A store built on context could not be reached from
 * there, and one built on an event emitter React can't subscribe to would tear.
 * `useSyncExternalStore` (in `session.tsx`) bridges the two.
 */

export interface SessionUser extends PolicySubject {
  id: string;
  email: string;
  role: Role;
  churchId: string;
  memberId: string | null;
  regionId: string | null;
}

export interface Session {
  accessToken: string;
  /** Epoch milliseconds, from the token's own `exp`. `null` if it carries none. */
  accessTokenExpiresAt: number | null;
  user: SessionUser;
}

export type SessionStatus =
  /** Boot has not finished asking the refresh cookie whether there is a session. */
  'unknown' | 'anonymous' | 'authenticated';

export interface SessionState {
  status: SessionStatus;
  session: Session | null;
}

const ANONYMOUS: SessionState = { status: 'anonymous', session: null };
const UNKNOWN: SessionState = { status: 'unknown', session: null };

let state: SessionState = UNKNOWN;
const listeners = new Set<() => void>();

function publish(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

export const sessionStore = {
  /** The current state. A stable reference between changes, as `useSyncExternalStore` requires. */
  getState(): SessionState {
    return state;
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Establish a session from a token pair the API just issued. */
  set(session: Session): void {
    publish({ status: 'authenticated', session });
  },

  /** No session — whether because nobody signed in, or because a refresh failed. */
  clear(): void {
    publish(ANONYMOUS);
  },

  /** Test-only: put the store back to its pre-boot state. */
  reset(): void {
    publish(UNKNOWN);
  },
};

/**
 * The API's token response, narrowed to what this app uses. The `refreshToken`
 * the body also carries is for the mobile client's secure storage; on the web
 * the same value arrives as an HttpOnly cookie the browser holds and this code
 * never sees. Reading it out of the body here would put the long-lived
 * credential back within reach of any XSS — the exact thing the cookie exists to
 * prevent — so the field is not in this type at all.
 */
export interface IssuedTokens {
  accessToken: string;
  user: { id: string; email: string };
}

/** Build a session from a token pair, or `null` if the access token is unreadable. */
export function sessionFromTokens(tokens: IssuedTokens): Session | null {
  const claims = decodeAccessToken(tokens.accessToken);
  if (!claims) return null;

  return {
    accessToken: tokens.accessToken,
    accessTokenExpiresAt: expiresAt(claims),
    user: {
      id: claims.sub,
      email: tokens.user.email,
      role: claims.role,
      churchId: claims.churchId,
      memberId: claims.memberId,
      regionId: claims.regionId,
    },
  };
}

/**
 * Hand the HTTP client its one way of reading the token (F0.5 left this seam
 * open for exactly this). Called once at module load: the client is a singleton
 * and so is the store, so there is nothing to tear down.
 */
setAccessTokenProvider(() => state.session?.accessToken ?? null);
